import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Heading,
  useToast,
  Button,
  HStack,
  VStack,
  Grid,
  GridItem,
  Text,
  Divider,
  Skeleton,
  Badge,
  Card,
  CardBody,
  CardHeader,
  Flex,
  useColorModeValue,
  Icon,
  Input,
  Select,
  Tooltip,
  IconButton,
  Alert,
  AlertIcon,
} from "@chakra-ui/react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  BsArrowLeft,
  BsPencil,
  BsCheckCircle,
  BsXCircle,
  BsArrowCounterclockwise,
  BsCheckCircleFill,
  BsXCircleFill,
  BsLightningChargeFill,
  BsInfoCircle,
} from "react-icons/bs";
import { toast as toastify } from "react-toastify";
import { useAuth } from "../../../context/AuthContext";
import procurementApi from "../services/procurementApi";
import StatusBadge from "../components/StatusBadge";
import RequestForm from "../components/RequestForm";
import LineItemsTable from "../components/LineItemsTable";
import VendorPOProposalList from "../components/VendorPOProposalList";
import ApprovalProgressBar from "../components/ApprovalProgressBar";
import CommentSection from "../components/CommentSection";
import "./RequestDetail.css";

const RequestDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const toast = useToast();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [request, setRequest] = useState(null);
  const [workflowVersions, setWorkflowVersions] = useState([]);
  const [selectedWorkflowId, setSelectedWorkflowId] = useState("");
  const [workflowVersionLoading, setWorkflowVersionLoading] = useState(false);
  const [workflowVersionSaving, setWorkflowVersionSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const benchRemarksRef = useRef("");
  const rejectRemarksRef = useRef("");
  const restartApprovalReasonRef = useRef("");

  const bgColor = useColorModeValue("white", "gray.800");
  const isRejected = request?.status === "REJECTED";
  const activeApprovalHistory = (request?.approvalHistory || []).filter(
    (entry) => !entry.superseded,
  );
  const hasApprovalStarted = activeApprovalHistory.length > 0;
  const activeApproverIds = (request?.activeApprovers || []).map(
    (approver) => approver?._id || approver,
  );
  const currentWorkflowStage =
    request?.workflowId?.stages?.[request.workflowStage];
  const currentWorkflowStageNumber = Number.isInteger(
    currentWorkflowStage?.stageNumber,
  )
    ? currentWorkflowStage.stageNumber
    : Number.isInteger(request?.workflowStage)
      ? request.workflowStage
      : 0;
  const canManageWorkflowVersionByRole = user
    ? ["SuperAdmin", "Admin", "Super Admin"].includes(user.role)
    : false;
  const canUpdateWorkflowVersion =
    canManageWorkflowVersionByRole && currentWorkflowStageNumber === 0;
  const isCurrentUserAnActiveApprover = user
    ? activeApproverIds.includes(user._id)
    : false;
  const canCurrentUserApprove =
    !!user &&
    (user.role === "SuperAdmin" ||
      isCurrentUserAnActiveApprover ||
      request?.currentApprover?._id === user._id);
  const canManageWorkflowVersion = [
    "DRAFT",
    "SENT_BACK",
    "SUBMITTED",
    "IN_REVIEW",
  ].includes(request?.status);
  const isWorkflowManager = user
    ? user.role === "SuperAdmin" ||
      ((request?.status === "DRAFT" || request?.status === "SENT_BACK") &&
        request?.createdBy?._id === user._id) ||
      ((request?.status === "SUBMITTED" || request?.status === "IN_REVIEW") &&
        canCurrentUserApprove)
    : false;
  const canShowWorkflowVersionControls =
    canManageWorkflowVersion &&
    (hasApprovalStarted ? isWorkflowManager : canUpdateWorkflowVersion);
  const shouldAutoOpenVendorQuotation =
    searchParams.get("openVendorQuotation") === "1";

  const handleVendorAutoOpenHandled = React.useCallback(() => {
    if (!shouldAutoOpenVendorQuotation) {
      return;
    }

    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.delete("openVendorQuotation");
    setSearchParams(nextSearchParams, { replace: true });
  }, [searchParams, setSearchParams, shouldAutoOpenVendorQuotation]);

  useEffect(() => {
    fetchRequest();
  }, [id]);

  useEffect(() => {
    if (!request?.unit || !request?.department || !request?.branch) {
      setWorkflowVersions([]);
      setSelectedWorkflowId("");
      return;
    }

    const currentWorkflowId = request.workflowId?._id || request.workflowId;
    const currentWorkflowVersion =
      request.workflowSnapshot?.version || request.workflowId?.version || 1;
    setSelectedWorkflowId(
      currentWorkflowId
        ? `${currentWorkflowId}:v${currentWorkflowVersion}`
        : "",
    );

    const loadWorkflowVersions = async () => {
      try {
        setWorkflowVersionLoading(true);
        const response = await procurementApi.getWorkflowVersions(
          request.unit,
          request.department,
          request.branch,
        );
        const payload = response?.data || response;
        const versions = Array.isArray(payload?.workflows)
          ? payload.workflows
          : [];
        setWorkflowVersions(versions);
      } catch (error) {
        setWorkflowVersions([]);
      } finally {
        setWorkflowVersionLoading(false);
      }
    };

    loadWorkflowVersions();
  }, [
    request?.unit,
    request?.department,
    request?.branch,
    request?.workflowId,
  ]);

  const fetchRequest = async () => {
    try {
      setLoading(true);
      const response = await procurementApi.getRequestById(id);
      const requestData = response?.data || response;
      if (!requestData) {
        throw new Error("Request not found");
      }
      setRequest(requestData);
    } catch (error) {
      toast({
        title: "Error fetching request",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      navigate("/procurement/requests");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
  };

  const handleInlineUpdate = async (formData) => {
    try {
      setUpdateLoading(true);
      await procurementApi.updateRequest(id, {
        ...formData,
        requestUrlBase: window.location.origin,
      });

      toast({
        title: "Request updated successfully",
        status: "success",
        duration: 2500,
        isClosable: true,
      });

      setIsEditing(false);
      fetchRequest();
    } catch (error) {
      toast({
        title: "Error updating request",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      await procurementApi.submitRequest(id);
      toast({
        title: "Request submitted for approval",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      fetchRequest();
    } catch (error) {
      toast({
        title: "Error submitting request",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleUpdateWorkflowVersion = async () => {
    if (!selectedWorkflowId) {
      toast({
        title: "Select workflow version",
        description: "Please select a workflow version to continue.",
        status: "warning",
        duration: 2500,
        isClosable: true,
      });
      return;
    }

    const [workflowIdValue, workflowVersionValue] = selectedWorkflowId
      ? selectedWorkflowId.split(":v")
      : ["", ""];

    try {
      setWorkflowVersionSaving(true);
      await procurementApi.updateRequest(id, {
        workflowId: workflowIdValue,
        workflowVersion: workflowVersionValue
          ? Number(workflowVersionValue)
          : undefined,
      });

      toast({
        title: "Workflow version updated",
        status: "success",
        duration: 2500,
        isClosable: true,
      });

      fetchRequest();
    } catch (error) {
      const errorCode =
        error.response?.data?.code || error.response?.data?.error?.code;
      toast({
        title:
          errorCode === "APPROVAL_ALREADY_STARTED"
            ? "Approval already started"
            : "Error updating workflow version",
        description:
          error.response?.data?.message ||
          error.response?.data?.error?.message ||
          error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setWorkflowVersionSaving(false);
    }
  };

  const handleRestartApprovalWithWorkflow = () => {
    if (!selectedWorkflowId) {
      toast({
        title: "Select workflow version",
        description: "Please select a workflow version to continue.",
        status: "warning",
        duration: 2500,
        isClosable: true,
      });
      return;
    }

    const [workflowIdValue, workflowVersionValue] = selectedWorkflowId
      ? selectedWorkflowId.split(":v")
      : ["", ""];

    const executeRestartApproval = async (toastId) => {
      const reason = restartApprovalReasonRef.current?.trim();

      if (!reason) {
        toastify.error("Please provide a reason for restarting approval.");
        return;
      }

      toastify.dismiss(toastId);

      try {
        setWorkflowVersionSaving(true);
        await procurementApi.restartApprovalWithWorkflow(id, {
          workflowId: workflowIdValue,
          workflowVersion: workflowVersionValue
            ? Number(workflowVersionValue)
            : undefined,
          reason,
        });

        toastify.success("Approval restarted with new workflow");
        restartApprovalReasonRef.current = "";
        fetchRequest();
      } catch (error) {
        toastify.error(
          error.response?.data?.message ||
            error.response?.data?.error?.message ||
            error.message ||
            "Failed to restart approval",
        );
      } finally {
        setWorkflowVersionSaving(false);
      }
    };

    const toastId = toastify(
      ({ closeToast }) => (
        <Box>
          <Text fontWeight="bold" mb={2}>
            Restart approval with selected workflow?
          </Text>
          <Text fontSize="sm" mb={3}>
            Existing approvals will be preserved as superseded audit records and
            approval will restart from stage 1.
          </Text>
          <Input
            placeholder="Reason for restart"
            size="sm"
            mb={3}
            onChange={(e) => {
              restartApprovalReasonRef.current = e.target.value;
            }}
          />
          <HStack spacing={2}>
            <Button
              size="sm"
              colorScheme="orange"
              onClick={() => executeRestartApproval(toastId)}
            >
              Restart Approval
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                restartApprovalReasonRef.current = "";
                closeToast();
              }}
            >
              Cancel
            </Button>
          </HStack>
        </Box>
      ),
      {
        autoClose: false,
        closeOnClick: false,
        draggable: false,
        type: "warning",
      },
    );
  };

  const handleApprove = async () => {
    try {
      await procurementApi.approveRequest(id, { comments: "Approved" });
      toast({
        title: "Request approved",
        status: "success",
        duration: 2000,
      });
      fetchRequest();
    } catch (error) {
      toast({
        title: "Error approving request",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 3000,
      });
    }
  };

  const handleReject = () => {
    const executeReject = async (toastId) => {
      toastify.dismiss(toastId);
      const remarks = rejectRemarksRef.current?.trim();
      if (!remarks) {
        toastify.error("Please provide a rejection reason.");
        return;
      }

      try {
        await procurementApi.rejectRequest(id, { remarks });
        toastify.success("Request rejected");
        rejectRemarksRef.current = "";
        fetchRequest();
      } catch (error) {
        toastify.error(
          error.response?.data?.message ||
            error.message ||
            "Failed to reject request",
        );
      }
    };

    const toastId = toastify(
      ({ closeToast }) => (
        <Box>
          <Text fontWeight="bold" mb={2}>
            Reason for rejection:
          </Text>
          <Input
            placeholder="Enter rejection remarks"
            size="sm"
            mb={3}
            onChange={(e) => {
              rejectRemarksRef.current = e.target.value;
            }}
          />
          <HStack spacing={2}>
            <Button
              size="sm"
              colorScheme="red"
              onClick={() => executeReject(toastId)}
            >
              Reject
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                rejectRemarksRef.current = "";
                closeToast();
              }}
            >
              Cancel
            </Button>
          </HStack>
        </Box>
      ),
      {
        autoClose: false,
        closeOnClick: false,
        draggable: false,
        type: "warning",
      },
    );
  };

  const handleSendBack = () => {
    const executeSendBack = async (toastId) => {
      toastify.dismiss(toastId);
      const remarks = rejectRemarksRef.current?.trim();
      if (!remarks) {
        toastify.error("Please provide remarks before sending back.");
        return;
      }

      try {
        await procurementApi.performAction(id, {
          action: "SEND_BACK",
          remarks,
        });
        toastify.success("Request sent back for revision");
        rejectRemarksRef.current = "";
        fetchRequest();
      } catch (error) {
        toastify.error(
          error.response?.data?.message ||
            error.response?.data?.error?.message ||
            error.message ||
            "Failed to send request back",
        );
      }
    };

    const toastId = toastify(
      ({ closeToast }) => (
        <Box>
          <Text fontWeight="bold" mb={2}>
            Remarks for send back:
          </Text>
          <Input
            placeholder="Enter remarks"
            size="sm"
            mb={3}
            onChange={(e) => {
              rejectRemarksRef.current = e.target.value;
            }}
          />
          <HStack spacing={2}>
            <Button
              size="sm"
              colorScheme="orange"
              onClick={() => executeSendBack(toastId)}
            >
              Send Back
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                rejectRemarksRef.current = "";
                closeToast();
              }}
            >
              Cancel
            </Button>
          </HStack>
        </Box>
      ),
      {
        autoClose: false,
        closeOnClick: false,
        draggable: false,
        type: "warning",
      },
    );
  };

  const handleApproveBench = () => {
    const executeBenchApproval = async (toastId) => {
      toastify.dismiss(toastId);
      try {
        await procurementApi.approveBench(id, {
          remarks: benchRemarksRef.current || "",
        });
        toastify.success(
          "Request approved by bench — all workflow stages overridden",
        );
        benchRemarksRef.current = "";
        fetchRequest();
      } catch (error) {
        toastify.error(
          error.response?.data?.error?.message ||
            "Failed to perform bench approval",
        );
      }
    };

    const toastId = toastify(
      ({ closeToast }) => (
        <Box>
          <Text fontWeight="bold" mb={2}>
            Approve by Bench?
          </Text>
          <Text fontSize="sm" mb={3}>
            This will override all remaining approval stages.
          </Text>
          <Input
            placeholder="Remarks (optional)"
            size="sm"
            mb={3}
            onChange={(e) => {
              benchRemarksRef.current = e.target.value;
            }}
          />
          <HStack spacing={2}>
            <Button
              size="sm"
              colorScheme="yellow"
              onClick={() => executeBenchApproval(toastId)}
            >
              Confirm
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                benchRemarksRef.current = "";
                closeToast();
              }}
            >
              Cancel
            </Button>
          </HStack>
        </Box>
      ),
      {
        autoClose: false,
        closeOnClick: false,
        draggable: false,
        type: "warning",
      },
    );
  };

  if (loading) {
    return (
      <Box p={6}>
        <Skeleton height="40px" mb={4} />
        <Skeleton height="600px" />
      </Box>
    );
  }

  if (!request) {
    return (
      <Box p={6}>
        <Text>Request not found</Text>
      </Box>
    );
  }

  const workflowSteps = (request.workflowId?.stages || []).map(
    (stage, index) => {
      const benchApprovedByHistory = activeApprovalHistory.some(
        (entry) => entry.stage === index && entry.action === "BENCH_APPROVE",
      );

      const approvedByHistory = activeApprovalHistory.some(
        (entry) =>
          entry.stage === index &&
          (entry.action === "APPROVE" || entry.action === "BENCH_APPROVE"),
      );

      const hasActiveCurrentStage =
        request.status === "SUBMITTED" || request.status === "IN_REVIEW";

      const status = benchApprovedByHistory
        ? "bench"
        : approvedByHistory || request.status === "APPROVED"
          ? "completed"
          : hasActiveCurrentStage && index === request.workflowStage
            ? "current"
            : "pending";

      return {
        id: stage._id || `${stage.stageName}-${index}`,
        name: stage.stageName || `Stage ${index + 1}`,
        status,
      };
    },
  );

  const workflowVersion =
    request.workflowSnapshot?.version || request.workflowId?.version || 1;
  const workflowName =
    request.workflowSnapshot?.name || request.workflowId?.name || "Workflow";
  const canEditByRole = user
    ? ["SuperAdmin", "Admin", "Super Admin"].includes(user.role)
    : false;
  const canSubmitRequest = ["DRAFT", "SENT_BACK"].includes(request.status);
  const canEditRequest = !isRejected && (canSubmitRequest || canEditByRole);
  const isHeaderActionDisabled = isEditing || updateLoading;
  const editDisabledReason =
    request.status === "REJECTED"
      ? "This request is rejected and cannot be edited."
      : "Editing is available for Admin/SuperAdmin roles. Other users can edit only in Draft or Sent Back status.";

  return (
    <Box p={{ base: 3, md: 6 }}>
      {/* Header */}
      <Flex
        direction={{ base: "column", md: "row" }}
        justify="space-between"
        align={{ base: "stretch", md: "center" }}
        mb={6}
        gap={3}
      >
        <HStack mb={{ base: 2, md: 0 }}>
          <Button
            leftIcon={<BsArrowLeft />}
            variant="ghost"
            onClick={() => navigate("/procurement/requests")}
            size={{ base: "sm", md: "md" }}
          >
            Back
          </Button>
          <Heading size={{ base: "md", md: "lg" }}>Request Details</Heading>
        </HStack>
        <HStack
          flexWrap="wrap"
          justify={{ base: "flex-start", md: "flex-end" }}
        >
          {!isEditing && (
            <>
              {canEditRequest ? (
                <Button
                  leftIcon={<BsPencil />}
                  variant="outline"
                  onClick={handleEdit}
                  size={{ base: "sm", md: "md" }}
                >
                  Edit
                </Button>
              ) : (
                <Tooltip
                  label={editDisabledReason}
                  placement="bottom"
                  hasArrow
                  bg="gray.700"
                  color="white"
                  fontSize="sm"
                >
                  <Box>
                    <Button
                      leftIcon={<BsPencil />}
                      variant="outline"
                      size={{ base: "sm", md: "md" }}
                      isDisabled
                    >
                      Edit
                    </Button>
                  </Box>
                </Tooltip>
              )}
              {canSubmitRequest && (
                <Button
                  leftIcon={<BsCheckCircle />}
                  colorScheme="blue"
                  onClick={handleSubmit}
                  size={{ base: "sm", md: "md" }}
                >
                  {request.status === "SENT_BACK"
                    ? "Resubmit for Approval"
                    : "Submit for Approval"}
                </Button>
              )}
            </>
          )}
          {(request.status === "SUBMITTED" || request.status === "IN_REVIEW") &&
            canCurrentUserApprove && (
              <>
                <Button
                  leftIcon={<BsCheckCircle />}
                  colorScheme="green"
                  onClick={handleApprove}
                  size={{ base: "sm", md: "md" }}
                  isDisabled={isHeaderActionDisabled}
                >
                  Approve
                </Button>
                <Button
                  leftIcon={<BsXCircle />}
                  colorScheme="red"
                  onClick={handleReject}
                  size={{ base: "sm", md: "md" }}
                  isDisabled={isHeaderActionDisabled}
                >
                  Reject
                </Button>
                <Button
                  leftIcon={<BsArrowCounterclockwise />}
                  colorScheme="orange"
                  onClick={handleSendBack}
                  size={{ base: "sm", md: "md" }}
                  isDisabled={isHeaderActionDisabled}
                >
                  Send Back
                </Button>
                <Button
                  leftIcon={<BsLightningChargeFill />}
                  colorScheme="yellow"
                  onClick={handleApproveBench}
                  size={{ base: "sm", md: "md" }}
                  isDisabled={isHeaderActionDisabled}
                >
                  Approve by Bench
                </Button>
                <Tooltip
                  label={
                    <Box p={2}>
                      <Text fontWeight="bold" mb={2}>
                        Approval Button Visibility:
                      </Text>
                      <Text fontSize="sm" mb={1}>
                        • Status must be SUBMITTED or IN_REVIEW
                      </Text>
                      <Text fontSize="sm" mb={1}>
                        • You must be an active approver for this stage
                      </Text>
                      <Text fontSize="sm">• OR you have SuperAdmin role</Text>
                    </Box>
                  }
                  placement="bottom"
                  hasArrow
                  bg="gray.700"
                  color="white"
                  fontSize="sm"
                >
                  <IconButton
                    icon={<BsInfoCircle />}
                    variant="ghost"
                    size={{ base: "sm", md: "md" }}
                    aria-label="Approval button info"
                    colorScheme="blue"
                    isDisabled={isHeaderActionDisabled}
                  />
                </Tooltip>
              </>
            )}
          {(request.status === "SUBMITTED" || request.status === "IN_REVIEW") &&
            user &&
            !canCurrentUserApprove && (
              <Tooltip
                label={
                  <Box p={2}>
                    <Text fontWeight="bold" mb={2}>
                      Why can't I approve?
                    </Text>
                    <Text fontSize="sm" mb={1}>
                      • Approval buttons show to active stage approvers
                    </Text>
                    <Text fontSize="sm" mb={1}>
                      • Current Approver:{" "}
                      {request.currentApprover?.name || "N/A"}
                    </Text>
                    <Text fontSize="sm">
                      • SuperAdmins can approve at any stage
                    </Text>
                  </Box>
                }
                placement="bottom"
                hasArrow
                bg="orange.600"
                color="white"
                fontSize="sm"
              >
                <IconButton
                  icon={<BsInfoCircle />}
                  variant="ghost"
                  size={{ base: "sm", md: "md" }}
                  aria-label="Approval button info"
                  colorScheme="orange"
                  isDisabled={isHeaderActionDisabled}
                />
              </Tooltip>
            )}
        </HStack>
      </Flex>
      {isEditing && canEditRequest && (
        <Alert status="info" mb={6} borderRadius="md">
          <AlertIcon />
          Editing mode is active. Update or cancel from the form below to
          continue.
        </Alert>
      )}
      {isRejected && (
        <Alert status="warning" mb={6} borderRadius="md">
          <AlertIcon />
          This request has been rejected. All actions and edits are disabled.
        </Alert>
      )}
      <Box
        pointerEvents={isRejected ? "none" : "auto"}
        opacity={isRejected ? 0.65 : 1}
      >
        {isEditing && canEditRequest ? (
          <Card mb={6} bg={bgColor}>
            <CardHeader>
              <Heading size="md">Edit Request</Heading>
            </CardHeader>
            <CardBody>
              <RequestForm
                initialData={request}
                onSubmit={handleInlineUpdate}
                onCancel={handleCancelEdit}
                isLoading={updateLoading}
              />
            </CardBody>
          </Card>
        ) : (
          <>
            {/* Basic Info Card */}
            <Card mb={6} bg={bgColor}>
              <CardHeader>
                <Flex
                  direction={{ base: "column", sm: "row" }}
                  justify="space-between"
                  align={{ base: "flex-start", sm: "center" }}
                  gap={2}
                >
                  <Heading size={{ base: "sm", md: "md" }} noOfLines={2}>
                    {request.title}
                  </Heading>
                  <StatusBadge status={request.status} />
                </Flex>
              </CardHeader>
              <CardBody>
                <Grid
                  templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }}
                  gap={6}
                >
                  {request.IomGeneratedNumber && (
                    <GridItem>
                      <Text
                        fontWeight="semibold"
                        fontSize="sm"
                        color="gray.600"
                      >
                        IOM No.
                      </Text>
                      <Text fontFamily="mono">
                        {request.IomGeneratedNumber}
                      </Text>
                    </GridItem>
                  )}
                  <GridItem>
                    <Text fontWeight="semibold" fontSize="sm" color="gray.600">
                      Request ID
                    </Text>
                    <Text>
                      {request.requestId || request._id?.slice(-8) || "N/A"}
                    </Text>
                  </GridItem>
                  <GridItem>
                    <Text fontWeight="semibold" fontSize="sm" color="gray.600">
                      Department
                    </Text>
                    <Text>{request.department || "N/A"}</Text>
                  </GridItem>
                  <GridItem>
                    <Text fontWeight="semibold" fontSize="sm" color="gray.600">
                      Category
                    </Text>
                    <Text>{request.category || "N/A"}</Text>
                  </GridItem>
                  <GridItem>
                    <Text fontWeight="semibold" fontSize="sm" color="gray.600">
                      Priority
                    </Text>
                    {request.priority ? (
                      <Badge
                        colorScheme={
                          request.priority === "URGENT"
                            ? "red"
                            : request.priority === "HIGH"
                              ? "orange"
                              : request.priority === "MEDIUM"
                                ? "blue"
                                : "gray"
                        }
                      >
                        {request.priority}
                      </Badge>
                    ) : (
                      <Text>N/A</Text>
                    )}
                  </GridItem>
                  <GridItem>
                    <Text fontWeight="semibold" fontSize="sm" color="gray.600">
                      Estimated Budget
                    </Text>
                    <Text>₹{request.estimatedBudget || "N/A"}</Text>
                  </GridItem>
                  <GridItem>
                    <Text fontWeight="semibold" fontSize="sm" color="gray.600">
                      Expected Delivery
                    </Text>
                    <Text>
                      {request.expectedDeliveryDate
                        ? new Date(
                            request.expectedDeliveryDate,
                          ).toLocaleDateString()
                        : "N/A"}
                    </Text>
                  </GridItem>
                  <GridItem>
                    <Text
                      fontWeight="semibold"
                      fontSize="sm"
                      color="gray.600"
                      mb={1}
                    >
                      Workflow Version
                    </Text>
                    {request.status === "DRAFT" && canUpdateWorkflowVersion ? (
                      <VStack align="stretch" spacing={2}>
                        <Select
                          size="sm"
                          value={selectedWorkflowId}
                          onChange={(e) =>
                            setSelectedWorkflowId(e.target.value)
                          }
                          isDisabled={
                            workflowVersionLoading || workflowVersionSaving
                          }
                          placeholder="Select workflow version"
                        >
                          {workflowVersions.map((wf) => {
                            const optionKey =
                              wf.optionKey ||
                              `${wf.workflowId || wf._id}:v${wf.version || 1}`;
                            return (
                              <option key={optionKey} value={optionKey}>
                                {wf.name} - v{wf.version || 1}{" "}
                                {wf.isActive ? "(Active)" : "(Inactive)"}
                              </option>
                            );
                          })}
                        </Select>
                        <Button
                          size="sm"
                          colorScheme="purple"
                          variant="outline"
                          onClick={handleUpdateWorkflowVersion}
                          isLoading={workflowVersionSaving}
                          isDisabled={
                            workflowVersionLoading ||
                            workflowVersions.length === 0 ||
                            `${request.workflowId?._id || request.workflowId}:v${workflowVersion}` ===
                              selectedWorkflowId
                          }
                        >
                          Update Version
                        </Button>
                      </VStack>
                    ) : (
                      <HStack spacing={2}>
                        <Badge colorScheme="purple">{workflowName}</Badge>
                        <Badge colorScheme="teal">v{workflowVersion}</Badge>
                      </HStack>
                    )}
                  </GridItem>
                </Grid>

                <Divider my={4} />

                <VStack align="stretch" spacing={4}>
                  <Box>
                    <Text
                      fontWeight="semibold"
                      fontSize="sm"
                      color="gray.600"
                      mb={2}
                    >
                      Description
                    </Text>
                    {request.description && /[<>]/.test(request.description) ? (
                      <Box
                        className="description-content"
                        dangerouslySetInnerHTML={{
                          __html: request.description,
                        }}
                      />
                    ) : (
                      <Text>
                        {request.description || "No description provided"}
                      </Text>
                    )}
                  </Box>

                  {request.justification && (
                    <Box>
                      <Text
                        fontWeight="semibold"
                        fontSize="sm"
                        color="gray.600"
                        mb={2}
                      >
                        Justification
                      </Text>
                      {/[<>]/.test(request.justification) ? (
                        <Box
                          className="justification-content"
                          dangerouslySetInnerHTML={{
                            __html: request.justification,
                          }}
                        />
                      ) : (
                        <Text>{request.justification}</Text>
                      )}
                    </Box>
                  )}
                </VStack>
              </CardBody>
            </Card>

            {/* Workflow Stage Card - Show for submitted/approved/rejected requests */}
            {request.workflowId && request.status !== "DRAFT" && (
              <Card mb={6} bg={bgColor}>
                <CardHeader>
                  <Flex
                    justify="space-between"
                    align="center"
                    wrap="wrap"
                    gap={2}
                  >
                    <Heading size="md">Approval Workflow</Heading>
                    <VStack
                      align={{ base: "stretch", sm: "flex-end" }}
                      spacing={2}
                    >
                      {canManageWorkflowVersion &&
                        isWorkflowManager &&
                        hasApprovalStarted && (
                          <Text
                            fontSize="sm"
                            color="orange.500"
                            textAlign="right"
                          >
                            Approval already started. Use restart approval to
                            apply a new workflow version.
                          </Text>
                        )}
                      {canShowWorkflowVersionControls && (
                        <HStack
                          spacing={2}
                          flexWrap="wrap"
                          justify={{ base: "flex-start", sm: "flex-end" }}
                        >
                          <Select
                            size="sm"
                            minW={{ base: "220px", sm: "260px" }}
                            value={selectedWorkflowId}
                            onChange={(e) =>
                              setSelectedWorkflowId(e.target.value)
                            }
                            isDisabled={
                              workflowVersionLoading || workflowVersionSaving
                            }
                            placeholder="Select workflow version"
                          >
                            {workflowVersions.map((wf) => {
                              const optionKey =
                                wf.optionKey ||
                                `${wf.workflowId || wf._id}:v${wf.version || 1}`;
                              return (
                                <option key={optionKey} value={optionKey}>
                                  {wf.name} - v{wf.version || 1}{" "}
                                  {wf.isActive ? "(Active)" : "(Inactive)"}
                                </option>
                              );
                            })}
                          </Select>
                          {hasApprovalStarted ? (
                            <Button
                              size="sm"
                              colorScheme="orange"
                              variant="outline"
                              onClick={handleRestartApprovalWithWorkflow}
                              isLoading={workflowVersionSaving}
                              isDisabled={
                                workflowVersionLoading ||
                                workflowVersions.length === 0 ||
                                `${request.workflowId?._id || request.workflowId}:v${workflowVersion}` ===
                                  selectedWorkflowId
                              }
                            >
                              Restart Approval
                            </Button>
                          ) : (
                            canUpdateWorkflowVersion && (
                              <Button
                                size="sm"
                                colorScheme="purple"
                                variant="outline"
                                onClick={handleUpdateWorkflowVersion}
                                isLoading={workflowVersionSaving}
                                isDisabled={
                                  workflowVersionLoading ||
                                  workflowVersions.length === 0 ||
                                  `${request.workflowId?._id || request.workflowId}:v${workflowVersion}` ===
                                    selectedWorkflowId
                                }
                              >
                                Update Version
                              </Button>
                            )
                          )}
                        </HStack>
                      )}
                      <HStack spacing={2}>
                        <Badge colorScheme="purple">{workflowName}</Badge>
                        <Badge colorScheme="teal">v{workflowVersion}</Badge>
                      </HStack>
                    </VStack>
                  </Flex>
                </CardHeader>
                <CardBody>
                  {/* Current Stage Info */}
                  <VStack align="stretch" spacing={4} mb={6}>
                    <Flex
                      direction={{ base: "column", md: "row" }}
                      justify="space-between"
                      align={{ base: "stretch", md: "center" }}
                      gap={{ base: 4, md: 0 }}
                    >
                      <Box>
                        <Text fontSize="sm" color="gray.600" mb={1}>
                          Current Stage
                        </Text>
                        <Text
                          fontSize={{ base: "md", md: "lg" }}
                          fontWeight="semibold"
                        >
                          {request.workflowId?.stages?.[request.workflowStage]
                            ?.stageName || "N/A"}
                        </Text>
                      </Box>
                      {request.currentApprover && (
                        <Box textAlign={{ base: "left", md: "right" }}>
                          <Text fontSize="sm" color="gray.600" mb={1}>
                            Current Approver
                          </Text>
                          <Text fontSize="md" fontWeight="medium">
                            {request.currentApprover?.name || "N/A"}
                          </Text>
                          <Text fontSize="sm" color="gray.500">
                            {request.currentApprover?.email || ""}
                          </Text>
                        </Box>
                      )}
                    </Flex>

                    {/* Stage Progress Bar */}
                    {request.workflowId?.stages && (
                      <Box>
                        {request.workflowId.stages.length === 1 ? (
                          // Single Stage - Direct Approval
                          <VStack spacing={4} align="center" py={4}>
                            <Box textAlign="center">
                              <Badge colorScheme="blue" mb={3}>
                                Direct Approval
                              </Badge>
                            </Box>
                            <Icon
                              as={BsCheckCircleFill}
                              boxSize={16}
                              color="blue.500"
                            />
                            <Box textAlign="center">
                              <Text fontSize="lg" fontWeight="bold" mb={1}>
                                {request.workflowId.stages[0].stageName}
                              </Text>
                              <Text fontSize="sm" color="gray.600">
                                This request requires approval from a single
                                stage
                              </Text>
                            </Box>
                          </VStack>
                        ) : (
                          <ApprovalProgressBar steps={workflowSteps} />
                        )}
                      </Box>
                    )}
                  </VStack>

                  <Divider my={4} />

                  {/* Approval History Timeline */}
                  {request.approvalHistory &&
                    request.approvalHistory.length > 0 && (
                      <Box>
                        <Text fontWeight="semibold" fontSize="md" mb={4}>
                          Approval History
                        </Text>
                        <VStack align="stretch" spacing={4}>
                          {request.approvalHistory.map((history, index) => {
                            const isLast =
                              index === request.approvalHistory.length - 1;

                            return (
                              <Flex key={index} gap={4}>
                                <Box
                                  flexShrink={0}
                                  position="relative"
                                  display="flex"
                                  justifyContent="center"
                                  minW="20px"
                                >
                                  {!isLast && (
                                    <Box
                                      position="absolute"
                                      top="20px"
                                      bottom="-20px"
                                      left="50%"
                                      transform="translateX(-50%)"
                                      width="2px"
                                      bg="gray.300"
                                    />
                                  )}
                                  <Icon
                                    as={
                                      history.action === "APPROVE" ||
                                      history.action === "BENCH_APPROVE"
                                        ? BsCheckCircleFill
                                        : history.action === "REJECT"
                                          ? BsXCircleFill
                                          : BsArrowCounterclockwise
                                    }
                                    boxSize={5}
                                    color={
                                      history.action === "APPROVE"
                                        ? "green.500"
                                        : history.action === "BENCH_APPROVE"
                                          ? "yellow.500"
                                          : history.action === "REJECT"
                                            ? "red.500"
                                            : "orange.500"
                                    }
                                    bg={bgColor}
                                    zIndex={1}
                                  />
                                </Box>
                                <Box flex={1}>
                                  <Flex
                                    justify="space-between"
                                    align="center"
                                    mb={1}
                                  >
                                    <Text fontWeight="semibold" fontSize="sm">
                                      {history.stageName ||
                                        `Stage ${history.stage + 1}`}
                                    </Text>
                                    <Badge
                                      colorScheme={
                                        history.superseded
                                          ? "gray"
                                          : history.action === "APPROVE"
                                            ? "green"
                                            : history.action === "BENCH_APPROVE"
                                              ? "yellow"
                                              : history.action === "REJECT"
                                                ? "red"
                                                : "orange"
                                      }
                                    >
                                      {history.superseded
                                        ? "SUPERSEDED"
                                        : history.action === "BENCH_APPROVE"
                                          ? "BENCH APPROVE"
                                          : history.action}
                                    </Badge>
                                  </Flex>
                                  <Text fontSize="sm" color="gray.600">
                                    {history.approverId?.name || "Unknown"} (
                                    {history.approverId?.email})
                                  </Text>
                                  {history.superseded && (
                                    <Text fontSize="xs" color="gray.500">
                                      Superseded
                                      {history.supersededBy?.name
                                        ? ` by ${history.supersededBy.name}`
                                        : ""}
                                      {history.supersededReason
                                        ? `: ${history.supersededReason}`
                                        : ""}
                                    </Text>
                                  )}
                                  {history.action === "BENCH_APPROVE" &&
                                    history.benchApprovedBy && (
                                      <Text fontSize="xs" color="yellow.600">
                                        Bench approved by:{" "}
                                        {history.benchApprovedBy?.name ||
                                          "Unknown"}
                                      </Text>
                                    )}
                                  <Text fontSize="xs" color="gray.500">
                                    {new Date(
                                      history.actionDate || history.createdAt,
                                    ).toLocaleString()}
                                  </Text>
                                  {history.remarks && (
                                    <Text fontSize="sm" mt={2} color="gray.700">
                                      <strong>Remarks:</strong>{" "}
                                      {history.remarks}
                                    </Text>
                                  )}
                                </Box>
                              </Flex>
                            );
                          })}
                        </VStack>
                      </Box>
                    )}
                </CardBody>
              </Card>
            )}

            {/* Line Items Card */}
            <Card mb={6} bg={bgColor}>
              <CardHeader>
                <Heading size="md">Line Items</Heading>
              </CardHeader>
              <CardBody>
                <LineItemsTable
                  lineItems={request.lineItems || []}
                  setLineItems={() => {}}
                  readOnly={true}
                />
              </CardBody>
            </Card>

            {/* Vendor Quotation Section - Show for all statuses */}
            <Card mb={6} bg={bgColor}>
              <CardBody>
                <VendorPOProposalList
                  requestId={id}
                  requestStatus={request.status}
                  approvalHistory={request.approvalHistory || []}
                  canManageVendorQuotations={canCurrentUserApprove}
                  autoOpenAddProposal={shouldAutoOpenVendorQuotation}
                  onAutoOpenHandled={handleVendorAutoOpenHandled}
                  requestLineItems={request.lineItems || []}
                  onStatusChange={(newStatus) => {
                    setRequest({ ...request, status: newStatus });
                  }}
                />
              </CardBody>
            </Card>

            {/* Comments Section */}
            <Box mb={6}>
              <CommentSection requestId={id} request={request} />
            </Box>

            {/* Metadata */}
            <Card bg={bgColor}>
              <CardBody>
                <Grid
                  templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)" }}
                  gap={4}
                >
                  <GridItem>
                    <Text fontWeight="semibold" fontSize="sm" color="gray.600">
                      Created By
                    </Text>
                    <Text>{request.createdBy?.name || "N/A"}</Text>
                  </GridItem>
                  <GridItem>
                    <Text fontWeight="semibold" fontSize="sm" color="gray.600">
                      Created Date
                    </Text>
                    <Text>{new Date(request.createdAt).toLocaleString()}</Text>
                  </GridItem>
                  {request.updatedAt && (
                    <GridItem>
                      <Text
                        fontWeight="semibold"
                        fontSize="sm"
                        color="gray.600"
                      >
                        Last Updated
                      </Text>
                      <Text>
                        {new Date(request.updatedAt).toLocaleString()}
                      </Text>
                    </GridItem>
                  )}
                </Grid>
              </CardBody>
            </Card>
          </>
        )}
      </Box>
    </Box>
  );
};

export default RequestDetail;
